/**
 * Group citizenship ceremony capacity (SUP-2588).
 *
 * `GET {blApi}/citizenship/availability/{company}/{service}/{date}` answers one question
 * only: how many places are left on that ceremony — the service's
 * `citizenship_group_max` (or the per-date `citizenship_overrides_{client}` value) minus
 * the rows already in `citizenship_claims_{client}` for that datetime. It is never told
 * how many people this booking is for, so it can report capacity but never police it.
 *
 * One claim row is written per applicant (the confirmation page loops over
 * `lookupResponses`), so a party of N takes N places and the journey has to be the thing
 * that compares the two.
 */

export interface CitizenshipPartyInputs {
	/** Answer to "Who are you booking the citizenship ceremony for?" (Q481). */
	aloneAnswer: any;
	/** Answer to "How many people will be coming altogether..." (Q485). */
	statedApplicants: any;
	/** The company questions, used to count minors among the answered reference lookups. */
	questions: any[];
	/** `journey_config.appointments.general.minorsExcluded` for this client. */
	minorsExcluded: boolean;
}

/** The lambda tolerates a stringified boolean on the claim, so mirror that here. */
const isMinorLookup = (question: any) =>
	question?.lookupResponse?.minor === true || question?.lookupResponse?.minor === 'true';

/** A reference lookup that is still on the page and has come back with an answer. */
const isAnsweredLookup = (question: any) =>
	!!question?.aws_lookup && !!question?.visible && !!question?.lookupResponse;

/**
 * How many places on the ceremony this booking will consume.
 *
 * Minors are only skipped by the server-side count when the client config sets
 * `minorsExcluded`, so the same predicate is mirrored here — assuming either way would
 * silently over-block or, worse, overbook.
 */
export function citizenshipRequiredPlaces(inputs: CitizenshipPartyInputs): number {
	// The party-size question is hidden for "Only myself" and is not required even when
	// it is shown, so a missing answer means a party of one rather than something that
	// blocks the journey. Angular keeps the value of a hidden control, which is why the
	// "only myself" answer wins outright.
	const stated = parseInt(inputs?.statedApplicants, 10);
	const statedApplicants =
		inputs?.aloneAnswer === 'Only myself'
			? 1
			: !isNaN(stated) && stated > 0
				? stated
				: 1;

	// Each applicant gives their own Home Office reference, and it is those lookups that
	// become the claim rows the availability count is taken from. So where the party-size
	// question was left blank, the references still tell us how many people are coming —
	// take whichever number is higher rather than trusting a skipped question.
	const answeredLookups = (inputs?.questions || []).filter(isAnsweredLookup).length;
	const applicants = Math.max(statedApplicants, answeredLookups);

	// Only count minors we have actually confirmed through a reference lookup. A party
	// member we know nothing about is counted, which can only under-report availability.
	const minorCount = inputs?.minorsExcluded
		? (inputs?.questions || []).filter((q: any) => isAnsweredLookup(q) && isMinorLookup(q)).length
		: 0;

	return Math.max(1, applicants - minorCount);
}

/** Whether the party fits in what citizenshipAvailability reported. */
export function hasCapacityForParty(availability: any, requiredPlaces: number): boolean {
	const slots = Number(availability?.slots);
	if (!Number.isFinite(slots) || slots <= 0) {
		return false;
	}
	return requiredPlaces <= slots;
}

/** The slot count to show the customer, for an availability response of any shape. */
export function availableSlotsForDisplay(availability: any): number {
	const slots = Number(availability?.slots);
	return Number.isFinite(slots) ? slots : 0;
}

/**
 * The three answers a capacity check can give.
 *
 * `api.service.citizenshipAvailability` swallows every failure and returns the error
 * *message string*, so an unreachable endpoint arrives here looking exactly like a
 * response with no places left. That distinction does not matter on the calendar, where
 * the customer can simply pick a different time, but it matters a great deal on the
 * summary page: telling someone their ceremony is full when all that happened was a
 * failed request would send them away from a booking they could have completed.
 */
export type CitizenshipCapacityOutcome = 'available' | 'full' | 'unknown';

/**
 * Whether the party still fits, and whether we were actually told.
 *
 * 'unknown' means the endpoint gave us nothing we can compare against — the caller
 * should say nothing to the customer rather than invent a capacity problem.
 */
export function citizenshipCapacityOutcome(
	availability: any,
	requiredPlaces: number
): CitizenshipCapacityOutcome {
	if (!availability || typeof availability !== 'object') {
		return 'unknown';
	}
	if (!Number.isFinite(Number(availability.slots))) {
		return 'unknown';
	}
	return hasCapacityForParty(availability, requiredPlaces) ? 'available' : 'full';
}
